"use client";

import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { sendJson } from "@/components/send-json/send-json";
import { useNotify } from "@/components/toaster/app-toaster";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/** The box where a signed-in reader writes a comment (or an answer to one, with `parentId`); it waits for staff before anyone sees it. */
export function CommentForm({ postId, parentId, maxChars, onSent }: { postId: string; parentId?: string; maxChars: number; onSent?: () => void }) {
  const t = useTranslations("publicBlog.comments");
  const locale = useLocale();
  const notify = useNotify();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  async function send() {
    setBusy(true);
    const result = await sendJson("POST", "/api/blog/comments", { postId, parentId, locale, text });
    setBusy(false);
    if (result.ok) { setText(""); notify.success(t("sent")); onSent?.(); return; }
    notify.error(result.error === "too-many" ? t("tooMany") : t("error"));
  }
  return (
    <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      <Textarea value={text} maxLength={maxChars} rows={4} aria-label={t("write")} placeholder={t("write")} onChange={(e) => setText(e.target.value)} />
      <div className="flex justify-end"><Button type="submit" disabled={busy || !text.trim()}>{t("send")}</Button></div>
    </form>
  );
}

/** "Reply" under a comment: opens the comment box for an answer to it. */
export function ReplyBox({ postId, parentId, maxChars }: { postId: string; parentId: string; maxChars: number }) {
  const t = useTranslations("publicBlog.comments");
  const [open, setOpen] = useState(false);
  if (!open) return <Button type="button" variant="ghost" size="sm" className="self-start" onClick={() => setOpen(true)}>{t("reply")}</Button>;
  return <CommentForm postId={postId} parentId={parentId} maxChars={maxChars} onSent={() => setOpen(false)} />;
}
